import { NavLink, useNavigate, useLocation, Navigate, Outlet } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useRefresh } from "@/context/RefreshContext";
import { Shield, LayoutDashboard, FileText, UserX, Radio, Archive, LogOut, Link2, Crosshair, BookUser, UserCog, UserSquare2, FolderOpen, ShieldCheck, RefreshCw, Boxes, Map, Settings, ClipboardList, Megaphone, History, Calculator, Menu, ScrollText, Megaphone as MegaphoneIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AUTH_DISABLED } from "@/lib/constants";
import { usePermissions } from "@/lib/usePermissions";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import SpotifyPlayer from "@/components/SpotifyPlayer";
import api from "@/lib/api";

const NAV = [
  { to: "/", label: "Allgemeine Informationen", icon: FileText, end: true, testid: "nav-informationen", cap: "area.informationen" },
  { to: "/officer", label: "Officer", icon: UserCog, testid: "nav-officer", cap: "area.officer" },
  { to: "/appellthemen", label: "Appellthemen", icon: Megaphone, testid: "nav-appellthemen", cap: "area.appellthemen" },
  { to: "/fehlerliste", label: "Fehlerliste", icon: ClipboardList, testid: "nav-fehlerliste", cap: "area.fehlerliste" },
  { to: "/inhaftierung", label: "Inhaftierung", icon: UserX, testid: "nav-inhaftierung", cap: "area.inhaftierung" },
  { to: "/haftrechner", label: "Haftrechner", icon: Calculator, testid: "nav-haftrechner", cap: "area.haftrechner" },
  { to: "/langzeithaeftlinge", label: "Langzeithäftlinge", icon: UserSquare2, testid: "nav-langzeit", cap: "area.langzeit" },
  { to: "/einsaetze", label: "Einsätze", icon: Radio, testid: "nav-einsaetze", cap: "area.einsaetze" },
  { to: "/chaingangs", label: "Chain-Gangs", icon: Link2, testid: "nav-chaingangs", cap: "area.chaingangs" },
  { to: "/parole", label: "Parole", icon: Crosshair, testid: "nav-parole", cap: "area.parole" },
  { to: "/parole-log", label: "Parole-Log", icon: History, testid: "nav-parole-log", cap: "area.parolelog" },
  { to: "/verbrecherregister", label: "Verbrecherregister", icon: BookUser, testid: "nav-verbrecherregister", cap: "area.register" },
  { to: "/externe-dokumente", label: "Externe Dokumente", icon: FolderOpen, testid: "nav-externe-dokumente", cap: "area.externe" },
  { to: "/gefaengnisordnung", label: "Gefängnisordnung", icon: ScrollText, testid: "nav-gefaengnisordnung", cap: "area.gefaengnisordnung" },
  { to: "/lagerbestand", label: "Lagerbestand", icon: Boxes, testid: "nav-lagerbestand", cap: "area.lager" },
  { to: "/live-karte", label: "Gang-Karte", icon: Map, testid: "nav-live-karte", cap: "area.gangkarte" },
  { to: "/statistik", label: "Statistik", icon: LayoutDashboard, testid: "nav-statistik", cap: "area.statistik" },
  { to: "/archiv", label: "Archiv", icon: Archive, testid: "nav-archiv", cap: "area.archiv" },
  { to: "/administration", label: "Administration", icon: ShieldCheck, testid: "nav-administration", cap: "area.administration" },
];

// Route -> Bereichs-Capability (inkl. Aliase, die nicht in NAV stehen)
const ROUTE_CAP = {
  ...Object.fromEntries(NAV.map((n) => [n.to, n.cap])),
  "/informationen": "area.informationen",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { lastRefresh, refreshNow } = useRefresh();
  const navigate = useNavigate();
  const location = useLocation();
  const [pwOpen, setPwOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showMelde, setShowMelde] = useState(false);

  // Meldepflicht: Officer darf sich einloggen, sieht aber nur "Allgemeine Informationen".
  const meldepflicht = !!user?.meldepflicht;
  useEffect(() => {
    // Fenster bei jedem Login/Aufruf zeigen, solange Meldepflicht aktiv ist.
    if (meldepflicht) setShowMelde(true);
  }, [meldepflicht]);

  const { can, ready } = usePermissions();
  // Bereiche werden pro Rang freigeschaltet. Solange die Rechte laden, volle Nav.
  const nav = meldepflicht
    ? NAV.filter((item) => item.to === "/")
    : NAV.filter((item) => AUTH_DISABLED || !ready || can(item.cap));

  // Direkter URL-Aufruf gesperrter Bereiche: auf erste erlaubte Seite umleiten.
  const currentCap = ROUTE_CAP[location.pathname];
  const isInfoRoute = location.pathname === "/" || location.pathname === "/informationen";
  const routeBlocked = meldepflicht
    ? !isInfoRoute
    : (!AUTH_DISABLED && ready && currentCap && !can(currentCap));
  const fallbackTo = meldepflicht ? "/" : (nav.length ? nav[0].to : "/");

  const lastRefreshLabel = lastRefresh
    ? new Date(lastRefresh).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : "—";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background sadcr-noise">
      {/* Mobile Topbar */}
      <div className="lg:hidden fixed top-0 inset-x-0 h-14 z-30 flex items-center gap-3 px-4 border-b border-zinc-800 bg-[#0C0E12]">
        <button data-testid="sidebar-toggle" onClick={() => setSidebarOpen(true)} className="h-9 w-9 grid place-items-center rounded-sm text-zinc-300 hover:bg-zinc-800">
          <Menu className="h-5 w-5" />
        </button>
        <Shield className="h-5 w-5 text-primary" />
        <span className="font-heading font-black tracking-tight text-zinc-100">SADCR</span>
      </div>

      {/* Overlay (mobile) */}
      {sidebarOpen && <div data-testid="sidebar-overlay" onClick={() => setSidebarOpen(false)} className="lg:hidden fixed inset-0 bg-black/60 z-40" />}

      {/* Sidebar */}
      <aside className={`w-64 shrink-0 border-r border-zinc-800 bg-[#0C0E12] flex flex-col fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="h-16 flex items-center gap-3 px-5 border-b border-zinc-800">
          <div className="h-9 w-9 grid place-items-center bg-primary/15 border border-primary/40 rounded-sm">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          <div className="leading-none">
            <div className="font-heading font-black text-lg tracking-tight text-zinc-100">SADCR</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">
              Corrections System
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {nav.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors duration-150 border-l-2 ${
                  isActive
                    ? "border-primary bg-zinc-900 text-zinc-100 font-medium"
                    : "border-transparent text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50"
                }`
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-zinc-800 p-3">
          <button
            data-testid="global-refresh-btn"
            onClick={refreshNow}
            title="Seite jetzt aktualisieren"
            className="group w-full flex items-center gap-2.5 px-3 py-2 mb-2 rounded-sm border border-zinc-800 bg-[#0C0E12] hover:border-primary/50 transition-colors duration-150"
          >
            <RefreshCw className="h-3.5 w-3.5 text-zinc-500 group-hover:text-primary group-hover:rotate-180 transition-all duration-300" />
            <div className="text-left leading-tight">
              <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-400 group-hover:text-primary transition-colors duration-150">Aktualisieren</div>
              <div className="font-mono text-[9px] text-zinc-600">Stand {lastRefreshLabel}</div>
            </div>
          </button>
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-8 w-8 grid place-items-center bg-zinc-800 rounded-sm font-mono text-xs text-zinc-300">
              {(user?.dienstnummer || user?.name || "?").toString().slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-zinc-200 truncate">{user?.name}</div>
              <div className="font-mono text-[10px] text-zinc-500 truncate">DN {user?.dienstnummer || "—"}</div>
            </div>
            <button
              data-testid="open-change-password"
              onClick={() => setPwOpen(true)}
              title="Passwort ändern"
              className="h-8 w-8 grid place-items-center rounded-sm text-zinc-500 hover:text-primary hover:bg-primary/10 transition-colors duration-150"
            >
              <Settings className="h-4 w-4" />
            </button>
          </div>
          <Button
            data-testid="logout-btn"
            onClick={handleLogout}
            variant="ghost"
            className={`w-full justify-start gap-2 mt-1 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-sm ${AUTH_DISABLED ? "hidden" : ""}`}
          >
            <LogOut className="h-4 w-4" />
            Abmelden
          </Button>
          {AUTH_DISABLED && (
            <div className="mt-1 px-2 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-600">
              Login deaktiviert
            </div>
          )}
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 lg:ml-64 min-w-0 relative z-10 pt-14 lg:pt-0">
        <ErrorBoundary key={location.pathname}>
          {routeBlocked ? <Navigate to={fallbackTo} replace /> : <Outlet />}
        </ErrorBoundary>
      </main>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <SpotifyPlayer />

      {/* Meldepflicht-Fenster: erscheint bei jedem Login, solange Meldepflicht aktiv ist */}
      {meldepflicht && showMelde && (
        <div data-testid="meldepflicht-overlay" className="fixed inset-0 z-[100] grid place-items-center bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-md border border-sky-500/40 rounded-sm bg-[#0C0E12] shadow-2xl overflow-hidden">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-sky-500/30 bg-sky-500/10">
              <MegaphoneIcon className="h-6 w-6 text-sky-400 shrink-0" />
              <h2 className="font-heading font-black text-lg uppercase tracking-tight text-sky-200">Meldepflicht</h2>
            </div>
            <div className="px-6 py-6 space-y-3">
              <p className="text-base text-zinc-100 font-medium">Melden bei der Leitung</p>
              <p className="text-sm text-zinc-400">Bitte umgehend bei der Leitung melden: <span className="text-zinc-200 font-medium">Captain, Lieutenant, Sergeant II</span>.</p>
              <p className="text-xs text-zinc-500">Bis zur Freigabe durch die Leitung sind nur die Allgemeinen Informationen einsehbar.</p>
            </div>
            <div className="px-6 pb-6">
              <Button
                data-testid="meldepflicht-ack-btn"
                onClick={async () => { try { await api.post("/meldepflicht/ack"); } catch (e) { /* Protokoll ist unkritisch */ } setShowMelde(false); }}
                className="w-full rounded-sm bg-sky-600 hover:bg-sky-500 text-white font-mono font-bold text-base tracking-widest"
              >
                10-4
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
